import type { StringKey } from '@ipfabric/utils';
import type { FormikProps } from 'formik';
import type { JSX, ReactNode } from 'react';
import './styles.css';
import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import classNames from 'classnames';
import type { NestedFormikName } from 'utils/formik/nestedFormikName';

interface LabelValue {
  label: ReactNode;
  value: unknown;
}

interface Props<TFormikValues> {
  disabled?: boolean;
  formik: FormikProps<TFormikValues>;
  left: LabelValue;
  name: StringKey<TFormikValues> | NestedFormikName;
  right: LabelValue;
  tooltipTitle?: string;
}

export const FormikDoublePoleSwitch = <TFormikValues,>({
  disabled,
  formik,
  left,
  name,
  right,
  tooltipTitle,
}: Props<TFormikValues>): JSX.Element => {
  const field = formik.getFieldProps(name);

  const handleChange = (value: unknown) => {
    if (disabled) {
      return;
    }

    formik.setFieldValue(name as string, value);
  };

  const handleToggle = () => {
    if (disabled) {
      return;
    }

    if (field.value === left.value) {
      formik.setFieldValue(name as string, right.value);
    } else {
      formik.setFieldValue(name as string, left.value);
    }
  };

  return (
    <span className='formik-double-pole-switch'>
      <label
        htmlFor={undefined}
        className={classNames(
          'formik-double-pole-switch__label',
          disabled && 'formik-double-pole-switch__label--disabled',
        )}
        onClick={() => {
          handleChange(left.value);
        }}
      >
        {left.label}
      </label>
      <Tooltip content={tooltipTitle}>
        <span
          className={classNames(
            'formik-double-pole-switch__area',
            field.value !== null &&
              'formik-double-pole-switch__area--with-value',
            disabled && 'formik-double-pole-switch__area--disabled',
          )}
          onClick={handleToggle}
        >
          <span
            className={classNames(
              'formik-double-pole-switch__control',
              field.value === left.value &&
                'formik-double-pole-switch__control--left',
              field.value === right.value &&
                'formik-double-pole-switch__control--right',
              // Prevent tooltip on control element, it must be shown on area element
              tooltipTitle !== undefined && 'pointer-events-none',
            )}
          />
        </span>
      </Tooltip>
      <label
        htmlFor={undefined}
        className={classNames(
          'formik-double-pole-switch__label',
          disabled && 'formik-double-pole-switch__label--disabled',
        )}
        onClick={() => {
          handleChange(right.value);
        }}
      >
        {right.label}
      </label>
    </span>
  );
};
